// pages/HomePage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Search, ArrowRight, HelpCircle } from 'lucide-react';
import { faqService } from '../../services/faqService'; // Assurez-vous que le chemin est correct

const styles = {
  primaryColor: '#F76707',
  secondaryColor: '#0A6EBD',
  lightOrange: '#FFF4E6',
  gradient: 'linear-gradient(135deg, #F76707 0%, #0A6EBD 100%)',
};

const HomePage = () => {
  const [faqCategories, setFaqCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFaqs, setExpandedFaqs] = useState({});

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const result = await faqService.getAllCategories(true);
        if (result.success) {
          // Filtrer les catégories et items actifs
          const activeCategories = result.data
            .filter(category => category.isActive)
            .map(category => ({
              ...category,
              items: category.items
                ? category.items.filter(item => item.isActive).sort((a, b) => a.displayOrder - b.displayOrder)
                : []
            }))
            .filter(category => category.items.length > 0) // Ne montrer que les catégories avec des items
            .sort((a, b) => a.displayOrder - b.displayOrder);

          setFaqCategories(activeCategories);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des FAQ:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFaqs();
  }, []);

  const toggleFaq = (categoryId, itemId) => {
    setExpandedFaqs(prev => {
      const key = `${categoryId}-${itemId}`;
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      {/* Hero Section */}
      <div
        className="py-5 text-white text-center"
        style={{ background: styles.gradient }}
      >
        <div className="container py-5">
          <h1 className="display-4 fw-bold mb-4">
            Système de Gestion des Réclamations
          </h1>
          <p className="lead mb-0">
            Créez et suivez vos réclamations en toute simplicité
          </p>
        </div>
      </div>

      {/* Main Options */}
      <div className="container py-5">
        <div className="row justify-content-center g-4">
          {/* Créer une réclamation */}
          <div className="col-md-5">
            <div className="card h-100 border-0 shadow-sm hover-shadow">
              <div className="card-body text-center p-5">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                  style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: styles.lightOrange
                  }}
                >
                  <FileText
                    size={40}
                    style={{ color: styles.primaryColor }}
                  />
                </div>
                <h2 className="h4 mb-3" style={{ color: styles.primaryColor }}>
                  Créer une réclamation
                </h2>
                <p className="text-muted mb-4">
                  Déposez une nouvelle réclamation et obtenez un numéro de suivi unique
                </p>
                <Link
                  to="/create-reclamation"
                  className="btn btn-lg d-inline-flex align-items-center gap-2"
                  style={{
                    backgroundColor: styles.primaryColor,
                    color: 'white'
                  }}
                >
                  Commencer
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>

          {/* Suivre une réclamation */}
          <div className="col-md-5">
            <div className="card h-100 border-0 shadow-sm hover-shadow">
              <div className="card-body text-center p-5">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                  style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#E3F2FD'
                  }}
                >
                  <Search
                    size={40}
                    style={{ color: styles.secondaryColor }}
                  />
                </div>
                <h2 className="h4 mb-3" style={{ color: styles.secondaryColor }}>
                  Suivre une réclamation
                </h2>
                <p className="text-muted mb-4">
                  Consultez l'état d'avancement de votre réclamation existante
                </p>
                <Link
                  to="/tracking"
                  className="btn btn-lg d-inline-flex align-items-center gap-2"
                  style={{
                    backgroundColor: styles.secondaryColor,
                    color: 'white'
                  }}
                >
                  Rechercher
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Informations supplémentaires */}
        <div className="row mt-5">
          <div className="col-12">
            <div
              className="p-4 rounded-3"
              style={{ backgroundColor: styles.lightOrange }}
            >
              <h3 className="h5 mb-3" style={{ color: styles.primaryColor }}>
                Comment ça marche ?
              </h3>
              <div className="row g-4">
                <div className="col-md-4">
                  <div className="d-flex align-items-start">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: styles.primaryColor,
                        color: 'white',
                        flexShrink: 0
                      }}
                    >
                      1
                    </div>
                    <div>
                      <h4 className="h6 mb-2">Créez votre réclamation</h4>
                      <p className="text-muted small mb-0">
                        Remplissez le formulaire avec les détails de votre réclamation
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: styles.primaryColor,
                        color: 'white',
                        flexShrink: 0
                      }}
                    >
                      2
                    </div>
                    <div>
                      <h4 className="h6 mb-2">Recevez votre numéro</h4>
                      <p className="text-muted small mb-0">
                        Un numéro de suivi unique vous sera attribué
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-start">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: styles.primaryColor,
                        color: 'white',
                        flexShrink: 0
                      }}
                    >
                      3
                    </div>
                    <div>
                      <h4 className="h6 mb-2">Suivez l'avancement</h4>
                      <p className="text-muted small mb-0">
                        Consultez le statut de votre réclamation à tout moment
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section FAQ */}
        <div className="row mt-5">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3 d-flex align-items-center">
                <HelpCircle
                  size={24}
                  className="me-2"
                  style={{ color: styles.primaryColor }}
                />
                <h3 className="h5 mb-0">Foire Aux Questions</h3>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border" role="status" style={{ color: styles.primaryColor }}>
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2">Chargement des FAQ...</p>
                  </div>
                ) : faqCategories.length === 0 ? (
                  <div className="text-center p-4">
                    <p>Aucune FAQ disponible pour le moment.</p>
                  </div>
                ) : (
                  <div className="accordion accordion-flush" id="faqAccordion">
                    {faqCategories.map((category, catIndex) => (
                      <div key={category.id} className="mb-4">
                        <h4 className="h6 mb-3" style={{ color: styles.secondaryColor }}>
                          {category.name}
                        </h4>
                        {category.items.map((item, itemIndex) => {
                          const itemKey = `${category.id}-${item.id}`;
                          const isExpanded = !!expandedFaqs[itemKey];

                          return (
                            <div key={item.id} className="card mb-2 border">
                              <div
                                className="card-header bg-white py-3 px-4 d-flex justify-content-between align-items-center"
                                onClick={() => toggleFaq(category.id, item.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <h5 className="mb-0 h6">{item.question}</h5>
                                <ArrowRight
                                  size={20}
                                  style={{
                                    transform: isExpanded ? 'rotate(90deg)' : 'none',
                                    transition: 'transform 0.3s ease'
                                  }}
                                />
                              </div>
                              {isExpanded && (
                                <div className="card-body p-4">
                                  <div style={{ whiteSpace: 'pre-line' }}>
                                    {item.answer}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}

                <div className="text-center mt-4">
                  <Link
                    to="/faq"
                    className="btn btn-outline-primary"
                    style={{
                      color: styles.secondaryColor,
                      borderColor: styles.secondaryColor
                    }}
                  >
                    Voir toutes les questions
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>
        {`
        .hover-shadow {
          transition: all 0.3s ease;
        }

        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.175)!important;
        }
        
        .card-header {
          cursor: pointer;
        }
        
        .card-header:hover {
          background-color: #f8f9fa !important;
        }
        `}
      </style>
    </div>
  );
};

export default HomePage;